import { osnovaApi } from '../base';
import type { DeficitRow, LotApplication, MaterialLot, MtoAnomalyDecision, MtoComparison, MtoInfo, MtoMappingDecision, MtoMaterialDecision, MtoMaterialPosition, MtoMetrics, MtoOfferSelection, MtoPassportDecision, MtoRegistryImport, MtoRematchReport, MtoRun, MtoRunDecision, MtoRunDetail, MtoSupplierRegistryRow, NewLotApplication, NewMaterialLot, NomenclatureItem, QualityBatchItem, QualityDoc, QualityDocDecision, QualityDocValidity } from '@entities/mto/types';
import type { RdLibraryDocument, RdLibraryProject, VedomostFreshness } from '@entities/mto/vedomost';

export const mtoApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Карточка агента + перечень модулей (на каркасе — 6 модулей `planned`). */
    getMtoInfo: b.query<MtoInfo, void>({
      query: () => '/agents/mto/info',
      providesTags: ['Mto'],
    }),
    /** Метрики-заглушки агента (на каркасе — нули). */
    getMtoMetrics: b.query<MtoMetrics, void>({
      query: () => '/agents/mto/metrics',
      providesTags: ['Mto'],
    }),

    // ===================== ОСНОВА.Совещания =====================
    // Автопротоколирование совещаний (AI-PROTO-2026). Карточка агента витрины;
    // обработка за OSNOVA_MEETINGS_ENABLED на backend.

    /** Список прогонов (опц. фильтр по модулю). `scope: 'mine'` — только свои,
     *  иначе — все обработки отдела. Статус прогона меняется асинхронно —
     *  страница включает polling, пока есть created/processing. */
    listMtoRuns: b.query<
      { items: MtoRun[] },
      { module?: string; scope?: 'mine' | 'all'; limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/mto/runs', params: p ?? undefined }),
      providesTags: ['MtoRun'],
    }),

    // ---- Библиотека РД объектов (документы ПТО) для ведомости материалов ----

    /** Объекты библиотеки РД со сводкой по документам (снабжение видит все). */
    listMtoRdProjects: b.query<{ items: RdLibraryProject[] }, void>({
      query: () => '/agents/mto/rd-library/projects',
      providesTags: ['MtoRdLibrary'],
    }),

    /** Документы РД объекта с действующей редакцией и статусом разбора ПТО. */
    listMtoRdDocuments: b.query<{ items: RdLibraryDocument[] }, string>({
      query: (projectId) => `/agents/mto/rd-library/projects/${projectId}/documents`,
      providesTags: (_r, _e, projectId) => [{ type: 'MtoRdLibrary', id: projectId }],
    }),

    /** Ведомость материалов по выбранным документам библиотеки РД. */
    createMtoVedomostRun: b.mutation<
      { run_id: string },
      { project_id: string; revision_ids: string[] }
    >({
      query: (body) => ({ url: '/agents/mto/runs/vedomost', method: 'POST', body }),
      invalidatesTags: ['MtoRun'],
    }),

    /** Выгрузка результата обработки в Excel (Blob). Лениво — по клику. */
    exportMtoRun: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/mto/runs/${id}/export`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** Актуальность ведомости по РД: изменился ли разбор её документов в ПТО
     *  после формирования. Без тегов: проверка пересобирает ведомости
     *  документов, гонять её на каждое решение по строке незачем. */
    getMtoRunFreshness: b.query<VedomostFreshness, string>({
      query: (id) => `/agents/mto/runs/${id}/freshness`,
    }),

    /** Сформировать ведомость из библиотеки РД заново по текущему разбору ПТО;
     *  решения по неизменённым строкам переносятся. */
    reformMtoRun: b.mutation<{ run_id: string }, string>({
      query: (id) => ({ url: `/agents/mto/runs/${id}/reform`, method: 'POST' }),
      invalidatesTags: ['MtoRun'],
    }),

    /** Повторить обработку, завершившуюся ошибкой. */
    retryMtoRun: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/mto/runs/${id}/retry`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => ['MtoRun', { type: 'MtoRun', id }],
    }),

    /** Разобрать все неразобранные замечания обработки разом (опц. только видов
     *  `kinds`). */
    decideMtoRunAnomalies: b.mutation<
      { ok: boolean; updated: number },
      { id: string; decision: MtoAnomalyDecision; kinds?: string[] }
    >({
      query: ({ id, decision, kinds }) => ({
        url: `/agents/mto/runs/${id}/anomalies/decision`,
        method: 'POST',
        body: { decision, kinds: kinds ?? [] },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'MtoRun', id }],
    }),

    /** Карточка прогона: прогон + паспорта (1.1) + позиции потребности (1.2) +
     *  сопоставления с НСИ (1.3) + заказы/приходы (2.1) + длинноцикловые позиции
     *  (2.2) + аномалии. Поля-коллекции — `[]` для прогонов другого модуля. */
    getMtoRun: b.query<MtoRunDetail, string>({
      query: (id) => `/agents/mto/runs/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'MtoRun', id }],
    }),

    /** Создать прогон: multipart. Текстовое поле `module` (`passports` |
     *  `rd_extract`, по умолчанию `passports`) + файлы под именем поля = doc_type
     *  (`realizations`/`test_protocols` для 1.1, `rd` для 1.2). Принимает готовый
     *  FormData — произвольный набор полей собирает вызывающий. Возвращает id
     *  нового прогона. */
    createMtoRun: b.mutation<{ run_id: string }, FormData>({
      query: (form) => ({ url: '/agents/mto/runs', method: 'POST', body: form }),
      invalidatesTags: ['MtoRun'],
    }),

    deleteMtoRun: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/mto/runs/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MtoRun'],
    }),

    /** HITL: решение по прогону (accepted | rejected | disputed). 409, если
     *  есть неразобранные критические аномалии (показываем текст ошибки). */
    decideMtoRun: b.mutation<
      { ok: boolean; decision: string },
      { id: string; decision: MtoRunDecision }
    >({
      query: ({ id, decision }) => ({
        url: `/agents/mto/runs/${id}/decision`,
        method: 'PATCH',
        body: { decision },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'MtoRun', id }],
    }),

    /** HITL: решение по паспорту (accepted | rejected). `runId` — из карточки,
     *  backend не возвращает run_id, а инвалидировать нужно прогон. */
    decideMtoPassport: b.mutation<
      { ok: boolean },
      { id: string; runId: string; decision: MtoPassportDecision }
    >({
      query: ({ id, decision }) => ({
        url: `/agents/mto/passports/${id}`,
        method: 'PATCH',
        body: { decision },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }],
    }),

    /** HITL: решение по аномалии (acknowledged | rejected). `runId` — для
     *  инвалидации карточки прогона. */
    annotateMtoAnomaly: b.mutation<
      { ok: boolean },
      { id: string; runId: string; decision: MtoAnomalyDecision }
    >({
      query: ({ id, decision }) => ({
        url: `/agents/mto/anomalies/${id}`,
        method: 'PATCH',
        body: { decision },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }],
    }),

    /** HITL: решение по позиции потребности ТМЦ из РД (модуль 1.2):
     *  accepted | rejected | disputed. `runId` — из карточки, backend не
     *  возвращает run_id, а инвалидировать нужно прогон. */
    annotateMtoMaterial: b.mutation<
      { ok: boolean },
      { id: string; runId: string; decision: MtoMaterialDecision }
    >({
      query: ({ id, decision }) => ({
        url: `/agents/mto/materials/${id}`,
        method: 'PATCH',
        body: { decision },
      }),
      // Ведомость бывает на тысячи строк: решение по строке меняем в кэше
      // карточки сразу, без перезапроса всего прогона; при ошибке — откат.
      async onQueryStarted({ id, runId, decision }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          mtoApi.util.updateQueryData('getMtoRun', runId, (draft) => {
            const p = draft.materials.find((m) => m.id === id);
            if (p) p.decision = decision === 'reset' ? null : decision;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    /** Кратность строки «на 1 элемент», указанная снабженцем (`null` — снять).
     *  Меняет итоги ведомости «с учётом кратности» — карточку перезапрашиваем. */
    setMtoMaterialMultiplicity: b.mutation<
      { position: MtoMaterialPosition },
      { id: string; runId: string; factor: number | null }
    >({
      query: ({ id, factor }) => ({
        url: `/agents/mto/materials/${id}/multiplicity`,
        method: 'PATCH',
        body: { factor },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }],
    }),

    /** Скачать DOCX паспорта качества как Blob. RBAC — Authorization ставит
     *  baseQuery. `no-store` — документ мог быть пересобран, не отдаём из
     *  HTTP-кеша. Лениво (`useLazyExportMtoPassportQuery`): запрос по клику. */
    exportMtoPassport: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/mto/passports/${id}/download`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** Скачать DOCX проекта служебной записки по находкам прогона как Blob
     *  (модуль 2.5). Генерация on-demand на backend (сбор критических/важных
     *  аномалий → текст LLM → DOCX), потому `no-store`. Лениво
     *  (`useLazyExportMtoMemoQuery`): запрос по клику. */
    exportMtoMemo: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/mto/runs/${id}/memo`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ---- Модуль 1.3 «НСИ-сопоставление» ----
    // Справочник номенклатуры (НСИ): загрузка/размер. Сопоставление позиций
    // потребности идёт в составе прогона rd_extract (`getMtoRun.mappings`); HITL —
    // подтверждение/отклонение кандидата по сопоставлению.

    /** Размер справочника НСИ (число позиций). Тег `MtoNomenclature` — счётчик
     *  инвалидируется после импорта нового справочника. */
    getMtoNomenclature: b.query<{ count: number }, void>({
      query: () => '/agents/mto/nomenclature',
      providesTags: ['MtoNomenclature'],
    }),

    /** Просмотр/поиск справочника материалов: страница позиций + `total` под тем
     *  же фильтром. `q` — подстрока (имя/код/синоним); пусто — весь справочник.
     *  Инвалидируется тем же тегом `MtoNomenclature` (импорт нового справочника
     *  обновляет и список). */
    listMtoNomenclatureItems: b.query<
      { items: NomenclatureItem[]; total: number },
      { q?: string; limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/mto/nomenclature/items', params: p ?? undefined }),
      providesTags: ['MtoNomenclature'],
    }),

    /** Импорт справочника НСИ: multipart, один файл XLSX/PDF справочника
     *  номенклатуры. Принимает готовый FormData (файл кладёт вызывающий).
     *  Дополняет справочник: `imported` — добавлено, `duplicates` — уже были. */
    importMtoNomenclature: b.mutation<{ imported: number; duplicates: number }, FormData>({
      query: (form) => ({ url: '/agents/mto/nomenclature', method: 'POST', body: form }),
      invalidatesTags: ['MtoNomenclature'],
    }),

    /** HITL по сопоставлению с НСИ (модуль 1.3): `verified` (привязать кандидата,
     *  с `nomenclature_id`) | `rejected` | `pending`. `runId` — для инвалидации
     *  карточки прогона (backend не возвращает run_id в ответе). */
    verifyMtoMapping: b.mutation<
      { ok: boolean },
      { id: string; runId: string; nomenclature_id?: string | null; decision: MtoMappingDecision }
    >({
      query: ({ id, nomenclature_id, decision }) => ({
        url: `/agents/mto/mappings/${id}`,
        method: 'PATCH',
        body: { nomenclature_id: nomenclature_id ?? null, decision },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }],
    }),

    // ---- Сравнение КП (модуль 2.3, этап 2) ----

    /** Сравнительная таблица КП в контексте прогона: строки — позиции этого
     *  прогона, цены в ячейках — из ВСЕЙ базы КП за окно (`months`, по умолчанию
     *  полгода на backend). Отсюда история цен и тренд. Тег — карточка прогона:
     *  привязка позиции и выбор поставщика обновляют обе витрины разом. */
    getMtoRunComparison: b.query<MtoComparison, { id: string; months?: number }>({
      query: ({ id, months }) => ({
        url: `/agents/mto/runs/${id}/comparison`,
        params: months ? { months } : undefined,
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'MtoRun', id }],
    }),

    /** Ручная привязка позиции КП к номенклатуре — то, что не взял матчер.
     *  Backend заодно пополняет словарь синонимов, поэтому инвалидируем и
     *  справочник: следующая позиция с тем же текстом сматчится сама. */
    linkMtoOfferNomenclature: b.mutation<
      { ok: boolean },
      { id: string; runId: string; nomenclature_id: string }
    >({
      query: ({ id, nomenclature_id }) => ({
        url: `/agents/mto/offers/${id}/nomenclature`,
        method: 'PATCH',
        body: { nomenclature_id },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }, 'MtoNomenclature'],
    }),

    /** «Добавить в справочник»: позиции КП, которых нет в справочнике, — новой
     *  позицией; строки КП привязываются к ней, их написания — синонимы. */
    createMtoNomenclatureFromOffers: b.mutation<
      { nomenclature_id: string; linked: number },
      { runId: string; offer_ids: string[]; name: string; unit?: string | null }
    >({
      query: ({ offer_ids, name, unit }) => ({
        url: '/agents/mto/offers/to-nomenclature',
        method: 'POST',
        body: { offer_ids, name, unit: unit ?? null },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }, 'MtoNomenclature'],
    }),

    /** Реестр поставщиков с реквизитами и сводкой по КП. */
    listMtoSuppliers: b.query<{ items: MtoSupplierRegistryRow[] }, void>({
      query: () => '/agents/mto/suppliers',
      providesTags: ['MtoSupplier'],
    }),

    /** Загрузить реестр поставщиков файлом (поле `file`): новые заводятся,
     *  известные по КП — дополняются. */
    importMtoSuppliers: b.mutation<MtoRegistryImport, FormData>({
      query: (form) => ({ url: '/agents/mto/suppliers/import', method: 'POST', body: form }),
      invalidatesTags: ['MtoSupplier'],
    }),

    /** Поправить поставщика в реестре. */
    updateMtoSupplier: b.mutation<
      { ok: boolean },
      {
        id: string;
        name: string;
        inn: string | null;
        contact: string | null;
        specialization: string | null;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/mto/suppliers/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MtoSupplier'],
    }),

    /** Повторно подобрать КП к справочнику (после его дополнения и для старых
     *  КП) и дописать разбор цены. Без `runId` — вся база КП. */
    rematchMtoOffers: b.mutation<MtoRematchReport, { runId: string; scope?: 'run' | 'all' }>({
      query: ({ runId, scope }) => ({
        url: '/agents/mto/offers/rematch',
        method: 'POST',
        body: { run_id: scope === 'all' ? null : runId },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }],
    }),

    /** Зафиксировать выбор поставщика по позиции. Прошлые решения остаются
     *  историей — перезаписи нет, актуальным становится последнее. */
    selectMtoOffer: b.mutation<
      MtoOfferSelection,
      { id: string; runId: string; reason?: string | null }
    >({
      query: ({ id, reason }) => ({
        url: `/agents/mto/offers/${id}/selection`,
        method: 'POST',
        body: { reason: reason ?? null },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'MtoRun', id: runId }],
    }),

    // ---- Подфункция «Документы качества для АОСР» (AI-MTO-2026-QC, этап 1) ----
    // Прослеживаемость материалов для АОСР: реестр партий → документы качества по
    // партии → факты применения. Плюс два детерминированных отчёта (МТОК-4
    // действительность на дату работ, МТОК-6 дефицит). Все роуты под RBAC mto.

    /** Реестр партий материала (опц. фильтр по объекту `site_id`). */
    listMtoLots: b.query<{ items: MaterialLot[] }, { site_id?: string } | void>({
      query: (p) => ({ url: '/agents/mto/quality/lots', params: p ?? undefined }),
      providesTags: ['MtoLot'],
    }),

    /** Карточка партии: партия + её документы качества. Тег по `id` партии —
     *  инвалидируется при добавлении/удалении/решении документа этой партии. */
    getMtoLot: b.query<{ lot: MaterialLot; quality_docs: QualityDoc[] }, string>({
      query: (id) => `/agents/mto/quality/lots/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'MtoLot', id }],
    }),

    /** Создать партию (JSON). `nomenclature` обязательно. Партия влияет на
     *  отчёты — инвалидируем реестр (документы/применения у новой партии пусты,
     *  отчёты не меняются до их добавления). */
    createMtoLot: b.mutation<{ lot: MaterialLot }, NewMaterialLot>({
      query: (body) => ({ url: '/agents/mto/quality/lots', method: 'POST', body }),
      invalidatesTags: ['MtoLot'],
    }),

    /** Удалить партию (каскадом — её документы/применения). Инвалидируем реестр
     *  и оба отчёта (применения партии исчезают из дефицита). */
    deleteMtoLot: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/mto/quality/lots/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MtoLot', 'MtoApplication', 'MtoValidity', 'MtoDeficit'],
    }),

    /** Создать документ качества по партии (multipart: реквизиты + опц. файл
     *  PDF/фото). Принимает готовый FormData (поля собирает вызывающий: `lot_id`,
     *  `doc_type` обязательны; файл — под любым именем поля). Возвращает документ.
     *  Инвалидируем партию (по `lotId`) и отчёты действительности/дефицита. */
    createMtoQualityDoc: b.mutation<{ doc: QualityDoc }, { lotId: string; form: FormData }>({
      query: ({ form }) => ({ url: '/agents/mto/quality/docs', method: 'POST', body: form }),
      invalidatesTags: (_r, _e, { lotId }) => [
        { type: 'MtoLot', id: lotId },
        'MtoQualityDoc',
        'MtoValidity',
        'MtoDeficit',
      ],
    }),

    /** Массовое извлечение реквизитов документов качества (МТОК-2) для
     *  предпросмотра: multipart с любым числом файлов (поле `file`, по одному на
     *  файл) → массив распознанных реквизитов + подсказка привязки к партии.
     *  НИЧЕГО не сохраняет (тегов не инвалидирует) — импорт идёт `createMtoLot` +
     *  `createMtoQualityDoc` после правки в таблице предпросмотра. */
    extractMtoQualityBatch: b.mutation<{ items: QualityBatchItem[] }, FormData>({
      query: (form) => ({ url: '/agents/mto/quality/extract-batch', method: 'POST', body: form }),
    }),

    /** HITL по документу качества: подтвердить (`confirmed`) | вернуть в черновик
     *  (`draft`). `lotId` — для инвалидации карточки партии. */
    decideMtoQualityDoc: b.mutation<
      { ok: boolean },
      { id: string; lotId: string; decision: QualityDocDecision }
    >({
      query: ({ id, decision }) => ({
        url: `/agents/mto/quality/docs/${id}`,
        method: 'PATCH',
        body: { decision },
      }),
      invalidatesTags: (_r, _e, { lotId }) => [
        { type: 'MtoLot', id: lotId },
        'MtoQualityDoc',
        'MtoValidity',
      ],
    }),

    /** Удалить документ качества. Инвалидируем партию и отчёты — документ влияет
     *  на действительность/дефицит. */
    deleteMtoQualityDoc: b.mutation<{ ok: boolean }, { id: string; lotId: string }>({
      query: ({ id }) => ({ url: `/agents/mto/quality/docs/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { lotId }) => [
        { type: 'MtoLot', id: lotId },
        'MtoQualityDoc',
        'MtoValidity',
        'MtoDeficit',
      ],
    }),

    /** Скачать приложенный к документу качества файл как Blob. RBAC —
     *  Authorization ставит baseQuery; `no-store` — файл мог быть заменён.
     *  Лениво (`useLazyDownloadMtoQualityDocQuery`): запрос по клику. */
    downloadMtoQualityDoc: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/mto/quality/docs/${id}/download`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** Реестр фактов применения партий (опц. фильтр по объекту `site_id`). */
    listMtoApplications: b.query<{ items: LotApplication[] }, { site_id?: string } | void>({
      query: (p) => ({ url: '/agents/mto/quality/applications', params: p ?? undefined }),
      providesTags: ['MtoApplication'],
    }),

    /** Создать факт применения партии (JSON). `lot_id` обязателен. Инвалидируем
     *  реестр применений и отчёт дефицита (новое применение может дать дефицит). */
    createMtoApplication: b.mutation<{ application: LotApplication }, NewLotApplication>({
      query: (body) => ({ url: '/agents/mto/quality/applications', method: 'POST', body }),
      invalidatesTags: ['MtoApplication', 'MtoDeficit'],
    }),

    /** Удалить факт применения партии. Инвалидируем реестр и отчёт дефицита. */
    deleteMtoApplication: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/mto/quality/applications/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MtoApplication', 'MtoDeficit'],
    }),

    /** Отчёт действительности документов качества на дату работ (МТОК-4).
     *  Вердикт детерминированный (проверка срока действия), без ИИ. `work_date` —
     *  `YYYY-MM-DD`; по умолчанию (без параметра) backend берёт сегодня. */
    getMtoValidity: b.query<
      { work_date: string; items: QualityDocValidity[] },
      { work_date?: string } | void
    >({
      query: (p) => ({ url: '/agents/mto/quality/validity', params: p ?? undefined }),
      providesTags: ['MtoValidity'],
    }),

    /** Отчёт дефицита «применено по факту, но нет/просрочен документ качества»
     *  (МТОК-6). Опц. фильтр по объекту `site_id`. */
    getMtoDeficit: b.query<{ items: DeficitRow[] }, { site_id?: string } | void>({
      query: (p) => ({ url: '/agents/mto/quality/deficit', params: p ?? undefined }),
      providesTags: ['MtoDeficit'],
    }),

    /** Создать заказ на перевозку у Логиста из заказа МТО (`/orders/{id}/create-logist-order`). */
    createMtoLogistOrder: b.mutation<
      { order: { id: string; order_number?: string | null }; already_existed: boolean },
      {
        orderId: string;
        project_id?: string | null;
        contract_id?: string | null;
        loading_address?: string | null;
        unloading_address?: string | null;
        notes?: string | null;
      }
    >({
      query: ({ orderId, ...body }) => ({
        url: `/agents/mto/orders/${orderId}/create-logist-order`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogistOrder'],
    }),

    /** Создать партию материала в реестре качества из прихода МТО (`/deliveries/{id}/create-lot`). */
    createMtoLotFromDelivery: b.mutation<
      { lot: MaterialLot },
      { deliveryId: string; site_id?: string | null; supplier?: string | null }
    >({
      query: ({ deliveryId, ...body }) => ({
        url: `/agents/mto/deliveries/${deliveryId}/create-lot`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MtoLot'],
    }),

    // ===================== ОСНОВА.Руководитель =====================
    // Сводный отчёт о ходе строительства по объекту: KPI план/факт из контрактной
    // сметы, реестра КС и протоколов совещаний + пояснительная записка (narrative)
    // + предупреждения о нехватке данных. HITL на уровне прогона, выгрузка DOCX.

  }),
  overrideExisting: false,
});

export const {
  useGetMtoInfoQuery,
  useGetMtoMetricsQuery,
  useListMtoRunsQuery,
  useListMtoRdProjectsQuery,
  useListMtoRdDocumentsQuery,
  useCreateMtoVedomostRunMutation,
  useLazyExportMtoRunQuery,
  useRetryMtoRunMutation,
  useGetMtoRunFreshnessQuery,
  useReformMtoRunMutation,
  useDecideMtoRunAnomaliesMutation,
  useGetMtoRunQuery,
  useCreateMtoRunMutation,
  useDeleteMtoRunMutation,
  useDecideMtoRunMutation,
  useDecideMtoPassportMutation,
  useAnnotateMtoAnomalyMutation,
  useAnnotateMtoMaterialMutation,
  useSetMtoMaterialMultiplicityMutation,
  useLazyExportMtoPassportQuery,
  useLazyExportMtoMemoQuery,
  useGetMtoNomenclatureQuery,
  useListMtoNomenclatureItemsQuery,
  useImportMtoNomenclatureMutation,
  useVerifyMtoMappingMutation,
  useGetMtoRunComparisonQuery,
  useLinkMtoOfferNomenclatureMutation,
  useSelectMtoOfferMutation,
  useCreateMtoNomenclatureFromOffersMutation,
  useRematchMtoOffersMutation,
  useListMtoSuppliersQuery,
  useImportMtoSuppliersMutation,
  useUpdateMtoSupplierMutation,
  useListMtoLotsQuery,
  useGetMtoLotQuery,
  useCreateMtoLotMutation,
  useDeleteMtoLotMutation,
  useCreateMtoQualityDocMutation,
  useExtractMtoQualityBatchMutation,
  useDecideMtoQualityDocMutation,
  useDeleteMtoQualityDocMutation,
  useLazyDownloadMtoQualityDocQuery,
  useListMtoApplicationsQuery,
  useCreateMtoApplicationMutation,
  useDeleteMtoApplicationMutation,
  useGetMtoValidityQuery,
  useGetMtoDeficitQuery,
  useCreateMtoLogistOrderMutation,
  useCreateMtoLotFromDeliveryMutation,
} = mtoApi;
